import React, { useState, useEffect, useCallback } from 'react';
import {
  LEGAL_DOCUMENT_VERSION,
  LEGAL_PRIVACY_VERSION,
  LEGAL_TERMS_VERSION,
  hasAcceptedCurrentLegal,
} from './constants/legal';
import { normalizeDestination } from './utils/destinations';
import { normalizeMarketTrend } from './utils/marketSignals';
import { getAccountAccessError } from './utils/access';
import { datasetEntities, initialDeadlinesMap, initialPublishedHistory } from './data/dataset';
import { buildPublishedEntry } from './utils/aggregation';
import {
  acceptLegalTerms as acceptFirebaseLegalTerms,
  completeInitialPasswordChange as completeFirebaseInitialPasswordChange,
  createAdminNotification as createFirebaseAdminNotification,
  createCompanyUser as createFirebaseCompanyUser,
  deleteCompanyUser as deleteFirebaseCompanyUser,
  deleteSubmission as deleteFirebaseSubmission,
  firebaseEnabled,
  getUserProfile,
  listenToAuth,
  loginWithEmail,
  logoutFirebase,
  markAllAdminNotificationsRead as markAllFirebaseAdminNotificationsRead,
  registerCompany,
  reviewCompanyRegistration,
  removeDeadlineExtension,
  saveDeadline,
  saveDeadlineExtension,
  savePublishedResultWithArchive,
  saveSubmission,
  saveUserProfile,
  sendCompanyReminder as sendFirebaseCompanyReminder,
  subscribeBarometroData,
  testTransactionalEmail as testFirebaseTransactionalEmail,
  updateAdminNotification as updateFirebaseAdminNotification,
  updateCompanyUser as updateFirebaseCompanyUser,
  updateSubmission as updateFirebaseSubmission,
} from './services/firebase';

// Import UI subcomponents
import LoadingSpinner from './components/ui/LoadingSpinner';
import Notification from './components/ui/Notification';
import { resolvePrimaryCarrier } from './components/ui/NavieraDiagnostics';

// Import Auth subcomponent
import LoginScreen from './components/auth/LoginScreen';
import InitialPasswordChangeGate from './components/auth/InitialPasswordChangeGate';

// Import Legal subcomponents
import LegalConsentGate from './components/legal/LegalConsentGate';
import LegalDocumentPage from './components/legal/LegalDocumentPage';

// Import Dashboard subcomponents
import PublicDashboard from './components/dashboard/PublicDashboard';
import MethodologyPage from './components/dashboard/MethodologyPage';

// Import Layout subcomponent
import Layout from './components/layout/Layout';

// Import Admin subcomponent
import AdminDashboard from './components/admin/AdminDashboard';

// Import Company subcomponents
import CompanyDashboard from './components/company/CompanyDashboard';
import CompanyProfile from './components/company/CompanyProfile';
import CompanyRewards from './components/company/CompanyRewards';

// Import Helpers
import { normalizeSelectedPort } from './utils/helpers';

// --- Componente Principal APP ---
export default function App() {
  const [page, setPage] = useState('login'); // login, public_dashboard, admin_users, etc.
  const [currentUser, setCurrentUser] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: 'info' });
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [authReady, setAuthReady] = useState(!firebaseEnabled);
  const [firebaseDataError, setFirebaseDataError] = useState('');
  
  // --- Base de Datos MOCK (en Estado) ---
  const [allUsers, setAllUsers] = useState(datasetEntities.users);
  const [allSubmissions, setAllSubmissions] = useState(datasetEntities.submissions);
  const [publishedHistory, setPublishedHistory] = useState(initialPublishedHistory);
  	
  const [deadlines, setDeadlines] = useState(initialDeadlinesMap);
  const [deadlineExtensions, setDeadlineExtensions] = useState({});
  
  useEffect(() => {
    if (!firebaseEnabled) return undefined;

    let sessionVersion = 0;
    const unsubscribe = listenToAuth(async (authUser) => {
      const version = ++sessionVersion;
      setAuthReady(false);
      setFirebaseDataError('');
      try {
        if (!authUser) {
          setCurrentUser(null);
          setPage(prev => (prev.startsWith('public_') ? prev : 'login'));
          return;
        }

        const profile = await getUserProfile(authUser.uid);
        if (version !== sessionVersion) return;
        const accessError = getAccountAccessError(profile);
        if (accessError) {
          await logoutFirebase();
          setCurrentUser(null);
          setPage('login');
          setNotification({ message: accessError, type: 'error' });
          return;
        }

        setCurrentUser(profile);
        setPage(profile.role === 'admin' ? 'admin_submissions' : 'company_form');
      } catch (error) {
        if (version === sessionVersion) setFirebaseDataError(error.message || 'No se pudo cargar la sesión de Firebase.');
      } finally {
        if (version === sessionVersion) setAuthReady(true);
      }
    });
    return () => { sessionVersion++; unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!firebaseEnabled) return undefined;

    const role = currentUser?.role || 'public';
    const uid = currentUser?.uid || '';

    return subscribeBarometroData({
      role,
      uid,
      onData: (data) => {
        setPublishedHistory(data.publishedHistory);
        if (role === 'admin' || role === 'company') {
          setAllSubmissions(data.submissions);
          setDeadlines(data.deadlines);
          setDeadlineExtensions(data.deadlineExtensions);
        }
        if (role === 'admin') {
          setAllUsers(data.users);
          setAdminNotifications(data.notifications);
        }
        if (role === 'company' && data.companyProfileLoaded) {
          const accessError = getAccountAccessError(data.users[0]);
          if (accessError) {
            setCurrentUser(null);
            setPage('login');
            setAllUsers([]);
            setAllSubmissions([]);
            setNotification({ message: accessError, type: 'error' });
            logoutFirebase().catch(() => {});
            return;
          }
          setAllUsers(data.users);
          setCurrentUser(prev => (prev?.uid === data.users[0].uid ? { ...prev, ...data.users[0] } : prev));
        }
      },
      onError: (error) => {
        setFirebaseDataError(error.message || 'No se pudo sincronizar Firestore.');
      },
    });
  }, [currentUser?.role, currentUser?.uid]);

  // --- Handlers de Lógica (Simulan el Backend) ---

  const handleLogin = (user) => {
    setCurrentUser(user);
    if (user.role === 'public') setPage('public_dashboard');
    if (user.role === 'admin') setPage('admin_submissions');
    if (user.role === 'company') setPage('company_form');
  };

  const handleCredentialLogin = async ({ email, password }) => {
    // The auth observer owns session state, including revocation while login is in flight.
    await loginWithEmail(email, password);
  };

  const handleAcceptLegalTerms = async ({ userAgent = '' } = {}) => {
    const fallbackAcceptance = {
      version: LEGAL_DOCUMENT_VERSION,
      termsVersion: LEGAL_TERMS_VERSION,
      privacyVersion: LEGAL_PRIVACY_VERSION,
      acceptedAt: new Date().toISOString(),
      acceptedByUid: currentUser?.uid || currentUser?.id || '',
      acceptedByEmail: currentUser?.email || currentUser?.contactEmail || '',
      role: currentUser?.role || '',
      source: 'web_app',
      legalBasis: 'consent',
      userAgent,
    };

    const legalAcceptance = firebaseEnabled
      ? await acceptFirebaseLegalTerms({
          version: LEGAL_DOCUMENT_VERSION,
          termsVersion: LEGAL_TERMS_VERSION,
          privacyVersion: LEGAL_PRIVACY_VERSION,
          userAgent,
        })
      : fallbackAcceptance;

    const updatedUser = {
      ...currentUser,
      legalAcceptance: legalAcceptance || fallbackAcceptance,
    };

    setCurrentUser(updatedUser);
    if (!firebaseEnabled) {
      setAllUsers(prevUsers => prevUsers.map(user => (
        user.id === updatedUser.id ? updatedUser : user
      )));
    }
    setNotification({ message: 'Aceptación registrada correctamente.', type: 'success' });
  };

  const handleCompleteInitialPasswordChange = async (newPassword) => {
    if (!firebaseEnabled) return;
    const result = await completeFirebaseInitialPasswordChange(newPassword);
    if (!result?.changed) {
      throw new Error('No se pudo confirmar el cambio de contraseña.');
    }
    setCurrentUser(prev => ({
      ...prev,
      mustChangePassword: false,
      passwordChangedAt: result.changedAt || prev?.passwordChangedAt || new Date().toISOString(),
    }));
    setNotification({ message: 'Contraseña actualizada. Ya puedes continuar.', type: 'success' });
  };

  const handleLogout = async () => {
    if (firebaseEnabled && currentUser?.role !== 'public') {
      await logoutFirebase();
    }
    setCurrentUser(null);
    setPage('login');
  };

  const handleNav = (targetPage) => {
    setPage(targetPage);
  };

  const handleNotify = useCallback((message, type = 'info') => {
    setNotification({ message, type });
  }, []);

  const handleCreateAdminNotification = async ({ type = 'submission', title, message, company, targetPage, metadata = {} }) => {
    const createdAt = new Date().toISOString();
    const id = `admin_notification_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      type,
      title,
      message,
      companyId: company?.id || '',
      companyUid: company?.uid || '',
      companyName: company?.name || 'Empresa',
      createdAt,
      status: 'unread',
      targetPage,
      metadata,
    };

    if (firebaseEnabled && company?.uid) {
      await createFirebaseAdminNotification(payload);
      return;
    }

    setAdminNotifications(prev => [
      { id, ...payload },
      ...prev,
    ]);
  };

  const handleMarkNotificationRead = async (notificationId) => {
    if (firebaseEnabled) {
      await updateFirebaseAdminNotification(notificationId, { status: 'read', readAt: new Date().toISOString() });
      return;
    }
    setAdminNotifications(prev =>
      prev.map(item =>
        item.id === notificationId && item.status === 'unread'
          ? { ...item, status: 'read', readAt: new Date().toISOString() }
          : item
      )
    );
  };

  const handleResolveNotification = async (notificationId) => {
    if (firebaseEnabled) {
      await updateFirebaseAdminNotification(notificationId, { status: 'resolved', readAt: new Date().toISOString(), resolvedAt: new Date().toISOString() });
      return;
    }
    setAdminNotifications(prev =>
      prev.map(item =>
        item.id === notificationId
          ? { ...item, status: 'resolved', readAt: item.readAt || new Date().toISOString(), resolvedAt: new Date().toISOString() }
          : item
      )
    );
  };

  const handleMarkAllNotificationsRead = async () => {
    const readAt = new Date().toISOString();
    if (firebaseEnabled) {
      await markAllFirebaseAdminNotificationsRead();
      return;
    }
    setAdminNotifications(prev =>
      prev.map(item => (item.status === 'unread' ? { ...item, status: 'read', readAt } : item))
    );
  };
  
  const handleUpdateUser = async (updatedUserData) => {
    if (firebaseEnabled) {
      await saveUserProfile(updatedUserData);
      return;
    }
    setAllUsers(prevUsers => prevUsers.map(u => u.id === updatedUserData.id ? updatedUserData : u));
    setCurrentUser(prev => (prev?.id === updatedUserData.id ? updatedUserData : prev));
  };
  
  // Manejador para crear usuario (Admin)
  const handleCreateUser = async (newUser) => {
    const normalizedDestination = normalizeDestination(newUser.destination);
    if (firebaseEnabled) {
      return createFirebaseCompanyUser({
        id: newUser.id,
        name: newUser.name,
        email: newUser.email,
        temporaryPassword: newUser.temporaryPassword,
        exportType: newUser.exportType,
        destination: normalizedDestination,
        contactEmail: newUser.contactEmail,
        respondent: newUser.respondent,
      });
    }
    const user = { ...newUser, destination: normalizedDestination };
    setAllUsers(prevUsers => [...prevUsers, user]);
    return { user, welcomeEmailSent: null };
  };

  const handleUpdateCompany = async (company) => {
    const payload = {
      uid: company.uid || company.docId,
      name: company.name,
      email: company.email,
      contactEmail: company.contactEmail,
      respondent: company.respondent,
      exportType: company.exportType,
      destination: normalizeDestination(company.destination),
      active: company.active !== false,
    };
    if (firebaseEnabled) {
      return updateFirebaseCompanyUser(payload);
    }
    setAllUsers(prevUsers => prevUsers.map(user => (
      user.id === company.id ? { ...user, ...payload } : user
    )));
    return { user: { ...company, ...payload } };
  };

  const handleDeleteCompany = async (company) => {
    const uid = company.uid || company.docId;
    if (firebaseEnabled) {
      return deleteFirebaseCompanyUser(uid);
    }
    setAllUsers(prevUsers => prevUsers.filter(user => user.id !== company.id));
    setDeadlineExtensions(prev => Object.fromEntries(
      Object.entries(prev).map(([period, values]) => {
        const nextValues = { ...(values || {}) };
        delete nextValues[company.id];
        return [period, nextValues];
      }),
    ));
    return { deleted: true, uid, companyId: company.id };
  };

  const handleSendCompanyReminder = async (company, period) => {
    if (!firebaseEnabled) {
      return { sent: true, period, recipientEmail: company.contactEmail || company.email || '' };
    }
    return sendFirebaseCompanyReminder({
      uid: company.uid || company.docId,
      period,
    });
  };

  const handleTestTransactionalEmail = async (email) => {
    if (!firebaseEnabled) {
      throw new Error('La prueba de correo solo está disponible con Firebase activo.');
    }
    return testFirebaseTransactionalEmail(email);
  };
  
  // Manejador para setear deadline (Admin)
  const handleSetDeadline = async (month, date) => {
    if (firebaseEnabled) {
      await saveDeadline(month, date);
      return;
    }
    setDeadlines(prev => ({ ...prev, [month]: date }));
  };
  
  const handleSetDeadlineExtension = async (month, companyId, date) => {
    if (firebaseEnabled) {
      await saveDeadlineExtension(month, companyId, date, deadlineExtensions[month] || {});
      return;
    }
    setDeadlineExtensions(prev => ({
      ...prev,
      [month]: { ...(prev[month] || {}), [companyId]: date }
    }));
  };

  const handleRemoveDeadlineExtension = async (month, companyId) => {
    if (firebaseEnabled) {
      await removeDeadlineExtension(month, companyId, deadlineExtensions[month] || {});
      return;
    }
    setDeadlineExtensions(prev => {
      const nextMonth = { ...(prev[month] || {}) };
      delete nextMonth[companyId];
      return { ...prev, [month]: nextMonth };
    });
  };
  
  // Manejador para aprobar/rechazar (Admin)
  const handleUpdateSubmission = async (docId, status, observations) => {
    const existing = allSubmissions.find(sub => sub.docId === docId);
    const shouldAwardStar = existing && !existing.excludedFromResults && existing.status !== 'approved' && status === 'approved';
    const reviewEntry = { status, observations, at: new Date().toISOString() };
    const submissionPatch = {
      status,
      observations,
      reviewedAt: reviewEntry.at,
      history: [...(existing?.history || []), reviewEntry],
    };

    if (firebaseEnabled) {
      await updateFirebaseSubmission(docId, submissionPatch);
      if (shouldAwardStar && existing) {
        const user = allUsers.find(u => u.id === existing.companyId || u.uid === existing.companyUid);
        if (user) {
          await saveUserProfile({ ...user, stars: (user.stars || 0) + 1 });
        }
      }
      return;
    }
    
    setAllSubmissions(prev => 
      prev.map(sub => 
        sub.docId === docId 
          ? { 
              ...sub, 
              ...submissionPatch
            } 
          : sub
      )
    );
    
    if (shouldAwardStar && existing) {
      const user = allUsers.find(u => u.id === existing.companyId);
      if (user) {
        handleUpdateUser({ ...user, stars: (user.stars || 0) + 1 });
      }
    }
  };

  const adjustApprovedStar = async (submission, delta) => {
    if (!submission || submission.status !== 'approved' || delta === 0) return;
    const user = allUsers.find(u => u.id === submission.companyId || u.uid === submission.companyUid);
    if (!user) return;
    const nextStars = Math.max(0, (user.stars || 0) + delta);
    const updatedUser = { ...user, stars: nextStars };
    if (firebaseEnabled) {
      await saveUserProfile(updatedUser);
    } else {
      setAllUsers(prevUsers => prevUsers.map(u => (u.id === updatedUser.id ? updatedUser : u)));
      setCurrentUser(prev => (prev?.id === updatedUser.id ? updatedUser : prev));
    }
  };

  const handleSetSubmissionExclusion = async (docId, excluded) => {
    const existing = allSubmissions.find(sub => sub.docId === docId);
    if (!existing) throw new Error('No se encontró el envío.');

    const now = new Date().toISOString();
    const exclusionPatch = excluded
      ? {
          excludedFromResults: true,
          exclusionReason: 'Prueba / excluido por administrador',
          excludedAt: now,
          excludedBy: currentUser?.uid || currentUser?.id || '',
        }
      : {
          excludedFromResults: false,
          exclusionReason: '',
          excludedAt: '',
          excludedBy: '',
          restoredAt: now,
          restoredBy: currentUser?.uid || currentUser?.id || '',
        };

    if (firebaseEnabled) {
      await updateFirebaseSubmission(docId, exclusionPatch);
    } else {
      setAllSubmissions(prev => prev.map(sub => (
        sub.docId === docId ? { ...sub, ...exclusionPatch } : sub
      )));
    }

    if (existing.status === 'approved' && Boolean(existing.excludedFromResults) !== excluded) {
      await adjustApprovedStar(existing, excluded ? -1 : 1);
    }
  };

  const handleDeleteSubmission = async (docId) => {
    const existing = allSubmissions.find(sub => sub.docId === docId);
    if (!existing) throw new Error('No se encontró el envío.');

    if (firebaseEnabled) {
      await deleteFirebaseSubmission(docId);
    } else {
      setAllSubmissions(prev => prev.filter(sub => sub.docId !== docId));
    }

    if (existing.status === 'approved' && !existing.excludedFromResults) {
      await adjustApprovedStar(existing, -1);
    }
  };
	  
  // Manejador para publicar resultados (Admin)
  const handlePublishResults = async (monthFilter) => {
    const approvedSubmissions = allSubmissions.filter(s => s.status === 'approved' && s.month === monthFilter && !s.excludedFromResults);
    if (approvedSubmissions.length === 0) {
      handleNotify(`No hay envíos aprobados para el mes ${monthFilter} para publicar.`, 'error');
      return;
    }
    
    const publicEntry = buildPublishedEntry(approvedSubmissions, monthFilter);
    const archiveEntry = buildPublishedEntry(approvedSubmissions, monthFilter, { publicSafe: false });
    if (!publicEntry || !archiveEntry) {
      handleNotify(`No se pudieron calcular los resultados para ${monthFilter}.`, 'error');
      return;
    }
    
    if (firebaseEnabled) {
      await savePublishedResultWithArchive(monthFilter, publicEntry, {
        ...archiveEntry,
        approvedCount: approvedSubmissions.length,
        sourceSubmissionIds: approvedSubmissions.map((submission) => submission.docId).filter(Boolean),
        publishedBy: currentUser?.uid || currentUser?.id || '',
        publishedByEmail: currentUser?.email || currentUser?.contactEmail || '',
      });
    } else {
      setPublishedHistory(prev => ({ ...prev, [monthFilter]: publicEntry }));
    }
    handleNotify(`Resultados de ${monthFilter} publicados con éxito en el Dashboard Público`, 'success');
  };
  
  // Manejador para envío de formulario (Empresa)
  const resolveDeadlineForCompany = (month, companyId) => {
    const extension = deadlineExtensions[month]?.[companyId];
    return extension || deadlines[month];
  };

  const handleFormSubmit = async ({ formData, attachments = {}, editingSubmission }) => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const resolvedDeadline = resolveDeadlineForCompany(currentMonth, currentUser.id);
    if (resolvedDeadline) {
      const [year, month, day] = resolvedDeadline.split('-').map(Number);
      const endOfDay = new Date(Date.UTC(year, month - 1, day, 23, 59, 59));
      if (new Date() > endOfDay) {
        throw new Error('El plazo para este mes ha vencido. Solicita una prórroga al administrador.');
      }
    }
    
    const existingActive = allSubmissions.find(
      s => (
        s.companyId === currentUser.id &&
        s.month === currentMonth &&
        !s.excludedFromResults &&
        (!editingSubmission || s.docId !== editingSubmission.docId) &&
        s.status !== 'rejected'
      )
    );
    if (existingActive) {
      throw new Error('Ya existe un envío en revisión o aprobado para este mes.');
    }

    const submittedAt = new Date().toISOString();
    const subDocId = editingSubmission?.docId || `${currentUser.id}_${currentMonth}`;
    const marketTrend = normalizeMarketTrend(formData.d2_q5 || editingSubmission?.trend || '');
    const submissionPayload = editingSubmission
      ? {
          ...editingSubmission,
          data: formData,
          attachments,
          status: 'pending',
          observations: {},
          resubmittedAt: submittedAt,
          navieraPrincipal: resolvePrimaryCarrier(formData, editingSubmission.navieraPrincipal),
          port: normalizeSelectedPort(formData, editingSubmission.port || 'Otro puerto'),
          rentabilidadTrend: formData.d2_q4 || editingSubmission.rentabilidadTrend,
          trend: marketTrend,
        }
      : {
          docId: subDocId,
          companyId: currentUser.id,
          companyUid: currentUser.uid || currentUser.id,
          companyName: currentUser.name,
          exportType: currentUser.exportType,
          month: currentMonth,
          status: 'pending',
          data: formData,
          observations: {},
          attachments,
          submittedAt,
          history: [],
          destination: normalizeDestination(currentUser.destination),
          contactEmail: currentUser.contactEmail || currentUser.email || '',
          respondent: currentUser.respondent || '',
          navieraPrincipal: resolvePrimaryCarrier(formData),
          port: normalizeSelectedPort(formData),
          trend: marketTrend,
          rentabilidadTrend: formData.d2_q4 || '',
        };

    if (firebaseEnabled) {
      await saveSubmission(submissionPayload);
    } else if (editingSubmission) {
        setAllSubmissions(prev => prev.map(sub => 
          sub.docId === editingSubmission.docId
            ? submissionPayload
            : sub
        ));
      } else {
        setAllSubmissions(prev => [...prev, submissionPayload]);
      }

    await handleCreateAdminNotification({
      type: 'submission',
      title: editingSubmission ? 'Cuestionario corregido reenviado' : 'Nuevo cuestionario pendiente',
      message: `${currentUser.name} ${editingSubmission ? 'reenvió un cuestionario observado' : 'envió un nuevo cuestionario'} para ${currentMonth}. Requiere revisión administrativa antes de publicar resultados.`,
      company: currentUser,
      targetPage: 'admin_submissions',
      metadata: {
        month: currentMonth,
        docId: subDocId,
      },
    });
    handleNotify('Cuestionario enviado. Quedará pendiente de revisión.', 'success');
  };

  const renderPage = () => {
    if (page === 'login') {
      return (
        <LoginScreen
          onLogin={handleLogin}
          allUsers={allUsers}
          authMode={firebaseEnabled ? 'firebase' : 'mock'}
          onCredentialLogin={handleCredentialLogin}
          onRegister={registerCompany}
        />
      );
    }

    if (firebaseEnabled && !authReady && !page.startsWith('public_')) {
      return <LoadingSpinner fullScreen />;
    }

    if (firebaseEnabled && currentUser && currentUser.role !== 'public' && getAccountAccessError(currentUser)) {
      return <LoadingSpinner fullScreen />;
    }

    if (firebaseEnabled && currentUser?.role === 'company' && currentUser.mustChangePassword === true) {
      return (
        <InitialPasswordChangeGate
          currentUser={currentUser}
          onComplete={handleCompleteInitialPasswordChange}
          onLogout={handleLogout}
        />
      );
    }

    if (currentUser?.role && currentUser.role !== 'public' && !hasAcceptedCurrentLegal(currentUser)) {
      return (
        <LegalConsentGate
          currentUser={currentUser}
          onAccept={handleAcceptLegalTerms}
          onLogout={handleLogout}
        />
      );
    }
    
    // Todas las demás páginas usan el Layout
    let content = null;
    const currentMonth = new Date().toISOString().slice(0, 7);
    const companySubmissions = currentUser?.role === 'company' ? allSubmissions.filter(s => s.companyId === currentUser.id) : [];
    const resolvedDeadline = currentUser?.role === 'company' ? resolveDeadlineForCompany(currentMonth, currentUser.id) : undefined;
    const hasActiveSubmission = companySubmissions.some(s => s.month === currentMonth && !s.excludedFromResults && s.status !== 'rejected');
    const canSubmit = !hasActiveSubmission;
    
    switch (page) {
      case 'public_dashboard':
        content = (
          <PublicDashboard
            history={publishedHistory}
            enableAiNarrative={currentUser?.role === 'admin'}
            onNotify={handleNotify}
          />
        );
        break;
      case 'public_methodology':
        content = <MethodologyPage history={publishedHistory} />;
        break;
      case 'legal_privacy':
        content = <LegalDocumentPage type="privacy" />;
        break;
      case 'legal_terms':
        content = <LegalDocumentPage type="terms" />;
        break;
      
      // Admin
      case 'admin_activity':
      case 'admin_notifications':
      case 'admin_users':
      case 'admin_submissions':
      case 'admin_deadlines':
        content = (
          <AdminDashboard 
            allUsers={allUsers}
            allSubmissions={allSubmissions}
            deadlines={deadlines}
            deadlineExtensions={deadlineExtensions}
            onUpdateSubmission={handleUpdateSubmission}
            onSetSubmissionExclusion={handleSetSubmissionExclusion}
            onDeleteSubmission={handleDeleteSubmission}
            onPublishResults={handlePublishResults}
            onSetDeadline={handleSetDeadline}
            onSetExtension={handleSetDeadlineExtension}
            onRemoveExtension={handleRemoveDeadlineExtension}
            onCreateUser={handleCreateUser}
            onReviewRegistration={reviewCompanyRegistration}
            onUpdateCompany={handleUpdateCompany}
            onDeleteCompany={handleDeleteCompany}
            onSendCompanyReminder={handleSendCompanyReminder}
            onTestTransactionalEmail={handleTestTransactionalEmail}
            onNotify={handleNotify} 
            onNav={handleNav} 
            page={page}
            publishedHistory={publishedHistory}
            adminNotifications={adminNotifications}
            onMarkNotificationRead={handleMarkNotificationRead}
            onResolveNotification={handleResolveNotification}
            onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
          />
        );
        break;
        
      // Company
      case 'company_form':
        content = (
          <CompanyDashboard 
            currentUser={currentUser} 
            currentMonth={currentMonth}
            resolvedDeadline={resolvedDeadline} 
            onNotify={handleNotify} 
            onSubmit={handleFormSubmit}
            submissions={companySubmissions}
            canSubmit={canSubmit}
          />
        );
        break;
      case 'company_profile':
        content = (
          <CompanyProfile
            currentUser={currentUser}
            onNotify={handleNotify}
            onRequestAdminNotification={handleCreateAdminNotification}
          />
        );
        break;
      case 'company_rewards':
        content = <CompanyRewards stars={currentUser.stars} submissions={companySubmissions} />;
        break;

      default:
        // Si se pierde, volver a la página principal de su rol
        if (currentUser?.role === 'admin') setPage('admin_submissions');
        else if (currentUser?.role === 'company') setPage('company_form');
        else setPage('public_dashboard');
        content = <LoadingSpinner />;
    }
    
    return (
      <Layout 
        currentUser={currentUser} 
        onLogout={handleLogout} 
        onNav={handleNav}
        currentPage={page}
        adminUnreadCount={adminNotifications.filter((item) => item.status === 'unread').length}
      >
        {content}
      </Layout>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <Notification 
        message={notification.message || firebaseDataError}
        type={notification.message ? notification.type : 'error'}
        onDismiss={() => {
          setNotification({ message: '', type: 'info' });
          setFirebaseDataError('');
        }}
      />
      {renderPage()}
    </div>
  );
}
